import { Component, Element, Host, Listen, Prop, h } from '@stencil/core';
import { getAPI, type UserAPI } from '@sydle/ui-api';
import { Consumer } from '@sydle/ui-utils';
import { BlogPost } from '../../../types/blog';

@Component({
  tag: 'sy-blog-summary',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class SyBlogSummary {
  private consumer: Consumer;
  @Element() el: HTMLSyBlogSummaryElement;

  /**
   * Post body
   */
  @Prop() postBody: BlogPost;

  /**
   * An array of sections to be displayed in the summary.
   */
  @Prop() sections: Array<{ name: string | number | undefined; level: number }>;

  /**
   * Sets the title of the summary section.
   */
  @Prop() label = 'Summary';

  /**
   * Indicates whether the summary is expanded by default.
   */
  @Prop({ mutable: true, reflect: true }) expanded = false;

  /**
   * Defines the offset from the top when scrolling to a section.
   * Default value is 53.
   */
  @Prop() scrollOffset = 53;

  @Listen('syCollapseDidClose')
  collapseClosed(): void {
    this.expanded = false;
  }

  @Listen('syCollapseDidOpen')
  collapseOpened(): void {
    this.expanded = true;
  }

  connectedCallback() {
    this.consumer = new Consumer(this.el);
    this.consumer.subscribe('postBody', (post: BlogPost) => (this.postBody = post));
  }

  disconnectedCallback() {
    this.consumer.disconnect();
  }

  render() {
    if (
      !this.sections &&
      (!this.postBody?.sections?.length || (this.postBody?.isRichContent && !getAPI<UserAPI>('userAPI')?.getUser()))
    ) {
      this.el.style.display = 'none';
      return null;
    }
    this.sections = this.sections || this.postBody.sections;

    return (
      <Host>
        <div id="header">
          <sy-icon name={this.expanded ? 'remove' : 'add'} size="12" />
          <sy-text id="summary-label">{this.label}</sy-text>
        </div>
        <sy-collapse trigger-by="#header">
          {this.sections.map((section: { name: string | number | undefined; level: number }) => (
            <sy-blog-summary-node
              scrollOffset={this.scrollOffset}
              key={section.name}
              section-title={section.name}
              level={section.level - 2}
            />
          ))}
        </sy-collapse>
      </Host>
    );
  }
}
